import {
  Component,
  TemplateRef,
  input,
  contentChild,
  computed,
  viewChild,
  effect,
  output,
  untracked,
} from '@angular/core';
import { NgbTooltip, NgbTooltipModule, Placement } from '@ng-bootstrap/ng-bootstrap';

export enum TooltipPlacement {
  Top = 'top',
  TopStart = 'top-start',
  TopEnd = 'top-end',

  Bottom = 'bottom',
  BottomStart = 'bottom-start',
  BottomEnd = 'bottom-end',

  Left = 'left',
  LeftStart = 'left-start',
  LeftEnd = 'left-end',

  Right = 'right',
  RightStart = 'right-start',
  RightEnd = 'right-end',

  Auto = 'auto',
  AutoStart = 'auto-start',
  AutoEnd = 'auto-end',
}
@Component({
  selector: 'cwo-tooltip',
  standalone: true,
  imports: [NgbTooltipModule],
  template: `
    <div
      #tooltip="ngbTooltip"
      [ngbTooltip]="visible() ? tooltipContent() : null"
      [style.width]="width()"
      [placement]="placement()"
      [triggers]="triggers()"
      [popperOptions]="popperOptions()"
      [tooltipClass]="tooltipClass()"
      [autoClose]="autoClose()"
    >
      <ng-content></ng-content>
    </div>
  `
})
export class TooltipComponent {
  private static currentlyOpenTooltip: NgbTooltip | null = null;
  
  placement = input<Placement | Placement[]>(TooltipPlacement.Auto as Placement);
  autoClose = input<any>('true');
  tooltipClass = input<string>('');
  triggers = input<string>('hover focus');
  offsetY = input<number>(8);
  offsetX = input<number>(0);
  arrowPadding = input<number>();
  customTopPadding = input<any>();

  tooltipText = input<string | null>(null);
  width = input<string>('auto');
  visible = input<boolean>(true);
  customeBottomPadding = input<number>();
  closeTooltip = input<boolean>(false);
  resetCloseTooltipVal = output<void>();
  openTooltip = input<boolean>(false);
  tooltipClosed = output<void>();
  
  tooltipTemplate = contentChild('tooltipTemplate', { read: TemplateRef });
  tooltip = viewChild<NgbTooltip>('tooltip');
  anyTooltipActivity = output<void>();
  closeAllTooltips = input<object>({});

  tooltipContent = computed(() => {
    return this.tooltipTemplate() ?? this.tooltipText();
  });

  popperOptions = computed(() => (options: any) => ({
    ...options,
    modifiers: [
      ...(options?.modifiers || []),
      {
        name: 'offset',
        options: {
          offset: ({ placement }: { placement: string }) => {
            if (placement === TooltipPlacement.BottomEnd) {
              return [this.offsetX(), this.customeBottomPadding() || this.offsetY()];
            }

            return [this.offsetX(), this.offsetY()];
          },
        },
      },
      {
        name: 'arrow',
        options: {
          padding: this.arrowPadding(),
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: this.placement(),
          boundary: 'viewport',
          padding: this.customTopPadding(),
        },
      },
    ],
  }));

  constructor() {
    effect(() => {
      const tooltip = this.tooltip();

      untracked(() => {
        if(tooltip) {
          tooltip.shown.subscribe(() => {
            if (
              TooltipComponent.currentlyOpenTooltip &&
              TooltipComponent.currentlyOpenTooltip !== tooltip
            ) {
              TooltipComponent.currentlyOpenTooltip.close();
            }
    
            TooltipComponent.currentlyOpenTooltip = tooltip;
          });
    
          tooltip.hidden.subscribe(() => {
            if (TooltipComponent.currentlyOpenTooltip === tooltip) {
              TooltipComponent.currentlyOpenTooltip = null;
            }
            this.tooltipClosed.emit();
          });
        }
      });
    })

    effect(() => {
      const tooltip = this.tooltip();

      const closeOne = this.closeTooltip();
      const closeAll = this.closeAllTooltips();
    
      if (tooltip && (closeOne || closeAll)) {
        untracked(() => {
          tooltip.close();
          this.resetCloseTooltipVal.emit();
        });
      }
    });

    effect(() => {
      const openTooltip = this.openTooltip();

      if(openTooltip) {
        untracked(() => setTimeout(() => {
          this.tooltip()?.open();
        }));
      }
    })
  }
}
